import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  listAdminClients,
  createAdminClient,
  updateAdminClient,
  deleteAdminClient,
} from "../services/pdfService";
import { useI18n } from "../i18n";
import ClientThumb from "../components/ClientThumb";
import "./TemplatesPage.css";

function sanitizeEnvUrl(value) {
  if (!value) return "";
  return value.replace(/^["']|["']$/g, "").trim();
}

function resolveApiUrl() {
  const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";
  if (isLocal) return sanitizeEnvUrl(process.env.REACT_APP_API_URL_LOCAL || process.env.REACT_APP_API_URL);
  return sanitizeEnvUrl(process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_LOCAL);
}

const API_URL = resolveApiUrl();

const EMPTY_CLIENT_FORM = {
  client_name: "",
  template_name: "",
  grafana_url: "",
  grafana_api_key: "",
  roles: "",
};


const TemplatesPage = () => {
  const { instance, accounts } = useMsal();
  const { t } = useI18n();

  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_CLIENT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const logoDebounceRef = useRef(null);
  const fileInputRef = useRef(null);

  const getApiToken = useCallback(async () => {
    const result = await instance.acquireTokenSilent({
      scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
      account: accounts[0],
    });
    return result.accessToken;
  }, [accounts, instance]);

  const loadClients = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const token = await getApiToken();
      const data = await listAdminClients({ token });
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch (e) {
      setError(e.message || t("clients_load_error"));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [getApiToken, t]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const fetchLogo = useCallback((clientName) => {
    const name = (clientName || "").trim().toLowerCase();
    if (!name) {
      setLogoUrl(null);
      setLogoLoading(false);
      return;
    }
    setLogoLoading(true);
    clearTimeout(logoDebounceRef.current);
    logoDebounceRef.current = setTimeout(async () => {
      try {
        const token = await getApiToken();
        const res = await fetch(`${API_URL}/admin/clients/${encodeURIComponent(name)}/logo?t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (res.ok) {
          const blob = await res.blob();
          setLogoUrl(URL.createObjectURL(blob));
        } else {
          setLogoUrl(null);
        }
      } catch {
        setLogoUrl(null);
      } finally {
        setLogoLoading(false);
      }
    }, 500);
  }, [getApiToken]);

  useEffect(() => {
    if (!showForm) {
      setLogoUrl(null);
      setLogoLoading(false);
      return;
    }
    fetchLogo(formData.client_name);
    return () => clearTimeout(logoDebounceRef.current);
  }, [formData.client_name, showForm, fetchLogo]);

  const uploadLogo = useCallback(async (file) => {
    const name = formData.client_name.trim().toLowerCase();
    if (!name || !file) return;
    setLogoUploading(true);
    try {
      const token = await getApiToken();
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`${API_URL}/admin/clients/${encodeURIComponent(name)}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setFormError(err.error || "Upload failed");
        return;
      }
      fetchLogo(name);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setLogoUploading(false);
    }
  }, [formData.client_name, getApiToken, fetchLogo]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadLogo(file);
  }, [uploadLogo]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_CLIENT_FORM);
    setFormError(null);
    setShowApiKey(false);
    setShowForm(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setFormData({
      client_name: client.client_name,
      template_name: client.template_name || "",
      grafana_url: client.grafana_url,
      grafana_api_key: client.grafana_api_key || "",
      roles: (client.roles || []).join(", "),
    });
    setFormError(null);
    setShowApiKey(false);
    setShowForm(true);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSaving(true);

    if (!formData.client_name.trim()) { setFormError(t("clients_err_name")); setIsSaving(false); return; }
    if (!formData.grafana_url.trim()) { setFormError(t("clients_err_grafana_url")); setIsSaving(false); return; }

    const rolesList = formData.roles.split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      client_name: formData.client_name.trim(),
      template_name: formData.template_name.trim(),
      grafana_url: formData.grafana_url.trim(),
      grafana_api_key: formData.grafana_api_key.trim(),
      roles: rolesList,
    };

    try {
      const token = await getApiToken();
      if (editingId) {
        await updateAdminClient(editingId, payload, { token });
      } else {
        await createAdminClient(payload, { token });
      }
      setShowForm(false);
      setFormData(EMPTY_CLIENT_FORM);
      setEditingId(null);
      await loadClients({ silent: true });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [editingId, formData, getApiToken, loadClients, t]);

  const handleDelete = useCallback(async (client) => {
    if (deletingId) return;
    setDeletingId(client.id);
    try {
      const token = await getApiToken();
      await deleteAdminClient(client.id, { token });
      await loadClients({ silent: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  }, [getApiToken, loadClients, deletingId]);

  return (
    <div className="tpl-page">
      <div className="tpl-header">
        <h2 className="tpl-title">{t("templates_title")}</h2>
        <div className="tpl-header-actions">
          <span className="tpl-count">{clients.length} {t("templates_count_label")}</span>
          <button className="button-generate tpl-add-btn" onClick={openCreate}>+ {t("clients_add")}</button>
          <button className="button-generate" onClick={() => loadClients()} disabled={isLoading}>
            {isLoading ? (
              <span className="btn-loading-content"><span className="button-spinner" aria-hidden="true" />{t("loading")}</span>
            ) : t("refresh")}
          </button>
        </div>
      </div>

      {error && <p className="tpl-error">{error}</p>}

      {showForm && (
        <div className="tpl-form-overlay" onClick={() => setShowForm(false)}>
          <form className="tpl-form" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <h3>{editingId ? t("clients_edit") : t("clients_add")}</h3>
            {formError && <p className="tpl-form-error">{formError}</p>}

            <div className="tpl-form-columns">
              <div className="tpl-form-left">
                <label>{t("client")}</label>
                <input type="text" value={formData.client_name} onChange={(e) => setFormData((p) => ({ ...p, client_name: e.target.value }))} placeholder="carmignac" />

                <label>{t("templates_tpl_name")}</label>
                <input type="text" value={formData.template_name} onChange={(e) => setFormData((p) => ({ ...p, template_name: e.target.value }))} placeholder="Reporting Global" />

                <label>{t("clients_grafana_url")}</label>
                <input type="url" value={formData.grafana_url} onChange={(e) => setFormData((p) => ({ ...p, grafana_url: e.target.value }))} placeholder="https://client.supervision.open-lake.com" />

                <label>{t("clients_api_key")}</label>
                <div className="tpl-api-key-field">
                  <input type={showApiKey ? "text" : "password"} value={formData.grafana_api_key} onChange={(e) => setFormData((p) => ({ ...p, grafana_api_key: e.target.value }))} placeholder="glsa_..." />
                  <button type="button" className="tpl-toggle-key" onClick={() => setShowApiKey((v) => !v)} title={showApiKey ? "Masquer" : "Afficher"}>
                    {showApiKey ? "🙈" : "👁"}
                  </button>
                </div>

                <label>{t("clients_roles")} ({t("clients_roles_hint")})</label>
                <input type="text" value={formData.roles} onChange={(e) => setFormData((p) => ({ ...p, roles: e.target.value }))} placeholder="admin, clientname" />
              </div>

              <div className="tpl-form-right">
                <label>{t("templates_logo")}</label>
                <div
                  className={`tpl-logo-dropzone${isDragging ? " tpl-logo-dragover" : ""}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => formData.client_name.trim() && fileInputRef.current?.click()}
                >
                  {logoUploading && <span className="tpl-logo-loading">{t("loading")}...</span>}
                  {!logoUploading && logoLoading && <span className="tpl-logo-loading">{t("loading")}...</span>}
                  {!logoUploading && !logoLoading && logoUrl && (
                    <img src={logoUrl} alt="Logo client" className="tpl-logo-img" />
                  )}
                  {!logoUploading && !logoLoading && !logoUrl && (
                    <div className="tpl-logo-placeholder">
                      <span className="tpl-logo-icon">🖼</span>
                      {formData.client_name.trim()
                        ? <span>{t("templates_drop_logo")}</span>
                        : <span className="tpl-logo-none">{t("templates_enter_name_first")}</span>
                      }
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    style={{ display: "none" }}
                    onChange={(e) => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); e.target.value = ""; }}
                  />
                </div>
                {formData.client_name.trim() && !logoUploading && (
                  <button
                    type="button"
                    className="tpl-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!formData.client_name.trim()}
                  >
                    {t("templates_browse")}
                  </button>
                )}
              </div>
            </div>

            <div className="tpl-form-actions">
              <button type="button" className="tpl-cancel-btn" onClick={() => setShowForm(false)}>{t("cancel")}</button>
              <button type="submit" className="button-generate" disabled={isSaving}>
                {isSaving ? (
                  <span className="btn-loading-content"><span className="button-spinner" aria-hidden="true" />{t("loading")}</span>
                ) : editingId ? t("clients_save") : t("clients_create")}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tpl-table-wrap">
        <table className="tpl-table">
          <thead>
            <tr>
              <th>{t("client")}</th>
              <th>{t("templates_tpl_name")}</th>
              <th>{t("clients_grafana_url")}</th>
              <th>{t("clients_roles")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && clients.length === 0 && (
              <tr><td colSpan={5} className="tpl-empty">{t("clients_no_client")}</td></tr>
            )}
            {clients.map((client) => (
              <tr key={client.id}>
                <td className="tpl-client-name">
                  <div className="tpl-client-cell">
                    <ClientThumb clientName={client.client_name} getApiToken={getApiToken} size={24} />
                    <span>{client.client_name}</span>
                  </div>
                </td>
                <td>{client.template_name || "-"}</td>
                <td className="tpl-url-cell">{client.grafana_url}</td>
                <td>{(client.roles || []).join(", ") || "-"}</td>
                <td>
                  <div className="tpl-action-group">
                    <button className="tpl-edit-btn" onClick={() => openEdit(client)}>{t("coord_edit")}</button>
                    <button className="tpl-delete-btn" onClick={() => handleDelete(client)} disabled={deletingId === client.id}>
                      {deletingId === client.id ? t("deleting") : t("delete")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TemplatesPage;
